import { useMemo, useState } from 'react'
import { Link } from 'wouter'
import { ChevronDown, ChevronRight, Search } from 'lucide-react'
import { trpc } from '@/lib/trpc'

/**
 * Меню-каталог в боковой панели: три категории, собранные из живого каталога.
 *
 *   Игры              — пополнения игр (Fazer top-up) + Roblox (его склад — Robux);
 *   Пополнение        — TikTok Coins, Telegram Stars, Steam (сервисы сайта);
 *   Подарочные карты  — все остальные карты (Apple, PlayStation, Xbox, …).
 *
 * Ручные назначения из админки (`catalog_category_overrides`) переопределяют
 * автоматическое распределение. Ничего не дублируется: каждый товар попадает
 * ровно в одну категорию; ссылки ведут на его страницу.
 */

type GroupId = 'games' | 'topup' | 'cards'

interface MenuItem {
  id: string
  name: string
  href: string
}

const GROUP_TITLES: Record<GroupId, string> = {
  games: 'Игры',
  topup: 'Пополнение',
  cards: 'Подарочные карты',
}

const GROUP_HINTS: Record<GroupId, string> = {
  games: 'Пополнение игровых аккаунтов и Robux',
  topup: 'TikTok Coins, Telegram Stars, Steam',
  cards: 'Apple, PlayStation, Xbox и другие',
}

export default function CatalogMenu({
  enabled,
  overrides,
  onNavigate,
}: {
  enabled: boolean
  overrides: Record<string, string>
  onNavigate: () => void
}) {
  const { data: fz } = trpc.fazer.catalog.useQuery(undefined, {
    enabled,
    staleTime: 5 * 60 * 1000,
  })
  const [open, setOpen] = useState<GroupId | null>('games')
  const [query, setQuery] = useState('')

  const groups = useMemo(() => {
    const out: Record<GroupId, MenuItem[]> = {
      // сервисы сайта — постоянные пункты
      topup: [
        { id: 'svc-tiktok', name: 'TikTok Coins', href: '/p/tiktok' },
        { id: 'svc-stars', name: 'Telegram Stars', href: '/p/stars' },
        { id: 'svc-steam', name: 'Steam', href: '/p/steam' },
      ],
      games: [],
      cards: [],
    }
    if (!fz) return out

    const auto = (kind: string, id: string): GroupId => {
      const override = overrides[id]
      if (override === 'games' || override === 'topup' || override === 'cards') return override
      if (kind === 'topup') return 'games'
      if (id.startsWith('roblox')) return 'games'
      return 'cards'
    }

    for (const section of fz.sections) {
      if (section.kind === 'gamekey') continue
      for (const tile of section.tiles) {
        const group = auto(section.kind, tile.id)
        out[group].push({
          id: `${section.kind}:${tile.id}`,
          name: tile.name,
          href: `/p/fz~${section.kind}~${tile.id}`,
        })
      }
    }
    // В «Пополнении» сервисы идут первыми, затем игровые пополнения, если их
    // туда переназначили вручную.
    out.games.sort((a, b) => a.name.localeCompare(b.name, 'ru'))
    out.cards.sort((a, b) => a.name.localeCompare(b.name, 'ru'))
    return out
  }, [fz, overrides])

  if (!enabled) return null

  return (
    <div className="v2-catmenu">
      {(Object.keys(GROUP_TITLES) as GroupId[]).map(g => {
        const items = groups[g]
        const expanded = open === g
        const q = query.trim().toLowerCase()
        const visible = q && expanded ? items.filter(i => i.name.toLowerCase().includes(q)) : items
        return (
          <div key={g} className="v2-catmenu-group">
            <button
              type="button"
              className="v2-catmenu-head"
              onClick={() => { setOpen(expanded ? null : g); setQuery('') }}
              aria-expanded={expanded}
            >
              <span className="v2-catmenu-title">
                {GROUP_TITLES[g]}
                <span className="v2-catmenu-count">{items.length}</span>
              </span>
              {expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
            </button>
            <div className="v2-catmenu-hint">{GROUP_HINTS[g]}</div>
            {expanded && (
              <div className="v2-catmenu-body">
                {items.length > 12 && (
                  <label className="v2-catmenu-search">
                    <Search size={14} />
                    <input
                      value={query}
                      onChange={e => setQuery(e.target.value)}
                      placeholder="Найти в категории…"
                    />
                  </label>
                )}
                <div className="v2-catmenu-list">
                  {visible.map(item => (
                    <Link
                      key={item.id}
                      href={item.href}
                      className="v2-catmenu-item"
                      onClick={() => { onNavigate(); setQuery('') }}
                    >
                      <span>{item.name}</span>
                      <ChevronRight size={13} />
                    </Link>
                  ))}
                  {visible.length === 0 && <span className="v2-catmenu-empty">Ничего не нашлось</span>}
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
